// File: src/pages/DocsPage.tsx
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { marked } from 'marked';
import { useI18n } from '../i18n/I18nContext';
import { useLocalizedPath } from '../i18n/localeRouting';

// Create a local component that doesn't rely on the import
const DocsLayout = ({
  children,
  activeDoc,
  navLabels,
  localizeHref,
}: {
  children: React.ReactNode;
  activeDoc: string;
  navLabels: {
    readme: string;
    prd: string;
    strategy: string;
    nextSteps: string;
    techStack: string;
    privacy: string;
    terms: string;
  };
  localizeHref: (pathname: string) => string;
}) => {
  return (
    <div className="md:grid md:grid-cols-12 md:gap-8">
      <aside className="md:col-span-3 lg:col-span-2 mb-8 md:mb-0">
        <nav className="space-y-2">
          <Link
            to={localizeHref('/docs/readme')}
            className={`block w-full text-left px-4 py-2 rounded-md transition-all duration-300 ease-in-out transform ${
              activeDoc === 'readme'
                ? 'bg-validator-subtle text-validator-text font-semibold shadow-md scale-105'
                : 'text-secondary hover:bg-hover hover:shadow-lg hover:scale-105 hover:-translate-y-0.5'
            }`}
          >
            {navLabels.readme}
          </Link>
          <Link
            to={localizeHref('/docs/prd')}
            className={`block w-full text-left px-4 py-2 rounded-md transition-all duration-300 ease-in-out transform ${
              activeDoc === 'prd'
                ? 'bg-validator-subtle text-validator-text font-semibold shadow-md scale-105'
                : 'text-secondary hover:bg-hover hover:shadow-lg hover:scale-105 hover:-translate-y-0.5'
            }`}
          >
            {navLabels.prd}
          </Link>
          <Link
            to={localizeHref('/docs/strategy')}
            className={`block w-full text-left px-4 py-2 rounded-md transition-all duration-300 ease-in-out transform ${
              activeDoc === 'strategy'
                ? 'bg-validator-subtle text-validator-text font-semibold shadow-md scale-105'
                : 'text-secondary hover:bg-hover hover:shadow-lg hover:scale-105 hover:-translate-y-0.5'
            }`}
          >
            {navLabels.strategy}
          </Link>
          <Link
            to={localizeHref('/docs/next_steps')}
            className={`block w-full text-left px-4 py-2 rounded-md transition-all duration-300 ease-in-out transform ${
              activeDoc === 'next_steps'
                ? 'bg-validator-subtle text-validator-text font-semibold shadow-md scale-105'
                : 'text-secondary hover:bg-hover hover:shadow-lg hover:scale-105 hover:-translate-y-0.5'
            }`}
          >
            {navLabels.nextSteps}
          </Link>
          <Link
            to={localizeHref('/docs/tech_stack')}
            className={`block w-full text-left px-4 py-2 rounded-md transition-all duration-300 ease-in-out transform ${
              activeDoc === 'tech_stack'
                ? 'bg-validator-subtle text-validator-text font-semibold shadow-md scale-105'
                : 'text-secondary hover:bg-hover hover:shadow-lg hover:scale-105 hover:-translate-y-0.5'
            }`}
          >
            {navLabels.techStack}
          </Link>

          <div className="mt-3 pt-3 border-t border-subtle" />

          <Link
            to={localizeHref('/docs/privacy')}
            className={`block w-full text-left px-4 py-2 rounded-md transition-all duration-300 ease-in-out transform ${
              activeDoc === 'privacy'
                ? 'bg-validator-subtle text-validator-text font-semibold shadow-md scale-105'
                : 'text-secondary hover:bg-hover hover:shadow-lg hover:scale-105 hover:-translate-y-0.5'
            }`}
          >
            {navLabels.privacy}
          </Link>

          <Link
            to={localizeHref('/docs/terms')}
            className={`block w-full text-left px-4 py-2 rounded-md transition-all duration-300 ease-in-out transform ${
              activeDoc === 'terms'
                ? 'bg-validator-subtle text-validator-text font-semibold shadow-md scale-105'
                : 'text-secondary hover:bg-hover hover:shadow-lg hover:scale-105 hover:-translate-y-0.5'
            }`}
          >
            {navLabels.terms}
          </Link>
        </nav>
      </aside>
      <div className="md:col-span-9 lg:col-span-10 bg-surface p-6 rounded-lg border border-subtle">
        {children}
      </div>
    </div>
  );
};

export function DocsPage() {
  const { locale, t } = useI18n();
  const lp = useLocalizedPath();
  const { docName } = useParams<{ docName?: string }>();
  const activeDoc = docName || 'readme';
  const [content, setContent] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDoc = async () => {
      setLoading(true);
      setError(null);

      const basePathByDoc: Record<string, string> = {
        readme: '/README',
        prd: '/src/docs/prd',
        strategy: '/src/docs/strategy',
        next_steps: '/src/docs/next_steps',
        tech_stack: '/src/docs/tech_stack',
        privacy: '/src/docs/privacy',
        terms: '/src/docs/terms',
      };

      const basePath = basePathByDoc[activeDoc] ?? basePathByDoc.readme;
      const preferredPath = locale === 'es' ? `${basePath}.es.md` : `${basePath}.md`;
      const fallbackPath = `${basePath}.md`;

      try {
        console.log(`Fetching document from: ${preferredPath}`);
        let response = await fetch(preferredPath);

        if (!response.ok && preferredPath !== fallbackPath) {
          console.log(`Falling back to: ${fallbackPath}`);
          response = await fetch(fallbackPath);
        }

        if (!response.ok) {
          throw new Error(`Could not load ${preferredPath}. Status: ${response.status}`);
        }

        const text = await response.text();
        console.log(`Received markdown content (first 50 chars): ${text.substring(0, 50)}...`);

        // Parse the markdown to HTML
        const html = await marked.parse(text);

        // Make sure html is a string
        if (typeof html === 'string') {
          console.log(`Parsed HTML (first 50 chars): ${html.substring(0, 50)}...`);
          setContent(html);
        } else {
          console.error('Marked returned a non-string value:', html);
          setError('Error parsing markdown content');
        }
      } catch (e) {
        const message = e instanceof Error ? e.message : 'An unknown error occurred.';
        setError(message);
        console.error("Error fetching doc:", e);
      } finally {
        setLoading(false);
      }
    };

    fetchDoc();
  }, [activeDoc, locale]);

  const navLabels = {
    readme: t('docsNavReadme'),
    prd: t('docsNavPrd'),
    strategy: t('docsNavStrategy'),
    nextSteps: t('docsNavNextSteps'),
    techStack: t('docsNavTechStack'),
    privacy: t('docsNavPrivacy'),
    terms: t('docsNavTerms'),
  };

  return (
    <DocsLayout activeDoc={activeDoc} navLabels={navLabels} localizeHref={lp}>
      <article className="prose prose-lg max-w-none bg-surface p-6 rounded-lg shadow-md">
        {loading && <p>{t('docsLoading')}</p>}
        {error && <p className="text-error">{t('docsErrorPrefix')} {error}</p>}
        {!loading && !error && (
          <div dangerouslySetInnerHTML={{ __html: content }} className="pb-12" />
        )}
      </article>
    </DocsLayout>
  );
}